<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { CircleCheck } from 'lucide-vue-next';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import SatellitePreview from '@/components/customer-signup/SatellitePreview.vue';
import { useSignupStorage } from '@/composables/useSignupStorage';
import { servicePricing } from '@/routes';

const { signup } = useSignupStorage();

const fetchingPrice = ref(false);
const editingFrequency = ref(false);

const staticMapUrl = computed(() => {
    const lat = signup.melissa?.lat || signup.lat;
    const lng = signup.melissa?.lng || signup.lng;
    if (!lat || !lng) {
        return '';
    }
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&markers=color:red|${lat},${lng}&zoom=18&size=600x400&maptype=satellite&key=${key}`;
});

defineEmits<{
    (e: 'back'): void;
    (e: 'next'): void;
}>();

const dayNameMap: Record<number, string> = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
};

const mowAreaLabels: Record<string, string> = {
    front_and_back: 'Front & Back',
    front_only: 'Front Only',
    back_only: 'Back Only',
};

const subtotal = computed(() => signup.subtotal_mowing || signup.price || 0);
const tax = computed(() => signup.calculated_tax || signup.tax || 0);
const total = computed(
    () => signup.total_mowing || subtotal.value + tax.value || 0,
);
const lawnSqft = computed(
    () => signup.lawn_sqft || signup.analysis?.lawnSqft || signup.analysis?.mowableSqft || 0,
);

const formattedStartDate = computed(() => {
    if (!signup.start_date) {
        return '';
    }
    const date = new Date(signup.start_date + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
});

const mowDay = computed(() => {
    if (!signup.start_date) {
        return '';
    }
    return dayNameMap[new Date(signup.start_date + 'T00:00:00').getDay()];
});

const mowAreaLabel = computed(
    () => mowAreaLabels[signup.mow_areas] ?? signup.mow_areas,
);

const includedServices = [
    'Mow Lawn',
    'Line Trim',
    'Edge',
    'Blow Debris',
    '24/7 Text Support',
];

const optionalServices = computed(() => {
    const services = [
        { value: 'flower_bed_cleaning', label: 'Flower Bed Cleaning' },
        { value: 'bush_trimming', label: 'Bush Trimming' },
    ];

    if (signup.frequency !== 'Bi-Weekly') {
        services.push({ value: 'bag_grass', label: 'Bag Grass' });
    }

    return services;
});

function csrfToken(): string {
    return decodeURIComponent(
        document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? '',
    );
}

async function fetchPricing(): Promise<void> {
    if (!signup.frequency || !signup.mow_areas || !signup.zip) {
        return;
    }

    fetchingPrice.value = true;

    try {
        const response = await fetch(servicePricing.url(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-XSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({
                frequency: signup.frequency,
                mow_areas: signup.mow_areas,
                zip: signup.zip,
                lot_size: Number(signup.melissa?.size) || 0,
                living_space: Number(signup.melissa?.livingSpace) || 0,
                lawn_sqft: Number(signup.analysis?.lawnSqft) || 0,
                building_area: Number(signup.melissa?.buildingArea) || 0,
                bag_grass: signup.optional_services.includes('bag_grass'),
                corner_lot: signup.corner_lot === 'yes',
                date: signup.start_date || undefined,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            const pricing = data.pricing ?? {};
            signup.price = Number(pricing.price) || 0;
            signup.quote_price =
                Number(pricing.subtotalMowing) || Number(pricing.price) || 0;
            signup.tax = Number(pricing.tax) || 0;
            signup.tax_rate = Number(pricing.taxRate) || 0;
            signup.cost_service = Number(pricing.costService) || 0;
            signup.calculated_tax = Number(pricing.calculatedTax) || 0;
            signup.subtotal_mowing = Number(pricing.subtotalMowing) || 0;
            signup.total_mowing = Number(pricing.totalMowing) || 0;
        }
    } catch {
        // pricing unavailable
    } finally {
        fetchingPrice.value = false;
    }
}

function toggleOptionalService(value: string): void {
    if (signup.optional_services.includes(value)) {
        signup.optional_services = signup.optional_services.filter(
            (s: string) => s !== value,
        );
    } else {
        signup.optional_services = [...signup.optional_services, value];
    }

    if (value === 'bag_grass') {
        fetchPricing();
    }
}

function onFrequencyChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    signup.frequency = value;
    editingFrequency.value = false;

    if (
        value === 'Bi-Weekly' &&
        signup.optional_services.includes('bag_grass')
    ) {
        signup.optional_services = signup.optional_services.filter(
            (s: string) => s !== 'bag_grass',
        );
    }

    fetchPricing();
}

onMounted(() => {
    signup.quote_price = subtotal.value;
});
</script>

<template>
    <div>
        <div class="mb-[61px]">
            <div
                class="overflow-hidden rounded-xl border border-gray-200 shadow-sm"
            >
                <!-- Header -->
                <div class="flex items-start gap-4 px-8 pt-8 pb-6">
                    <div>
                        <h3 class="text-2xl font-bold text-mm-green">
                            My Custom Lawn<br />Mowing Plan
                        </h3>
                        <div class="mt-4">
                            <div class="flex items-baseline gap-2">
                                <span class="text-5xl font-bold text-mm-dark">
                                    <Spinner
                                        v-if="fetchingPrice"
                                        class="inline size-8"
                                    />
                                    <template v-else
                                        >${{ subtotal }}</template
                                    >
                                </span>
                                <span class="text-sm text-gray-500">{{
                                    signup.frequency?.toLowerCase() || 'weekly'
                                }}</span>
                            </div>
                            <div
                                v-if="!fetchingPrice && tax > 0"
                                class="mt-1 text-sm text-gray-500"
                            >
                                + ${{ tax.toFixed(2) }} tax = ${{
                                    Number.isInteger(total)
                                        ? total
                                        : total.toFixed(2)
                                }} total
                            </div>
                        </div>
                    </div>
                    <div
                        class="size-[177px] shrink-0 overflow-hidden rounded-lg bg-gray-200"
                    >
                        <SatellitePreview
                            v-if="signup.ai_available && signup.satellite_image"
                            :image="signup.satellite_image"
                            :polygons="signup.polygons"
                        />
                        <img
                            v-else-if="staticMapUrl"
                            :src="staticMapUrl"
                            alt="Property satellite view"
                            class="size-full object-cover"
                        />
                    </div>
                </div>

                <!-- Summary Row 1: Address / Sqft / Start Date -->
                <div class="px-8 py-5">
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Address
                            </p>
                            <p class="mt-1 text-sm text-mm-dark">
                                {{ signup.address_1 }}
                            </p>
                            <p class="text-sm text-gray-500">
                                {{ signup.city }}, {{ signup.state }}
                                {{ signup.zip }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Lawn Square Footage
                            </p>
                            <p class="mt-1 text-sm text-mm-dark">
                                {{
                                    lawnSqft > 0
                                        ? lawnSqft.toLocaleString()
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Start Date
                            </p>
                            <p class="mt-1 text-sm text-mm-dark">
                                {{ formattedStartDate }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Summary Row 2: Frequency / Area / Day -->
                <div class="mx-8 border-t border-gray-200" />
                <div class="px-8 py-5">
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Mow Frequency
                            </p>
                            <div v-if="editingFrequency" class="mt-1">
                                <select
                                    :value="signup.frequency"
                                    class="w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-mm-dark focus:border-mm-green focus:ring-1 focus:ring-mm-green focus:outline-none"
                                    @change="onFrequencyChange"
                                >
                                    <option
                                        v-for="freq in signup.available_frequencies"
                                        :key="freq.value"
                                        :value="freq.value"
                                    >
                                        {{ freq.label }}
                                    </option>
                                </select>
                            </div>
                            <div
                                v-else
                                class="mt-1 flex items-baseline gap-1.5"
                            >
                                <span class="text-sm text-mm-dark">
                                    {{ signup.frequency }}
                                </span>
                                <button
                                    type="button"
                                    class="cursor-pointer text-xs font-bold text-mm-green uppercase hover:underline"
                                    @click="editingFrequency = true"
                                >
                                    Change
                                </button>
                            </div>
                        </div>
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Mow Area
                            </p>
                            <p class="mt-1 text-sm text-mm-dark">
                                {{ mowAreaLabel }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xs font-bold tracking-[1px] text-mm-dark uppercase"
                            >
                                Mow Day
                            </p>
                            <p class="mt-1 text-sm text-mm-dark">
                                {{ mowDay }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Included Services -->
                <div class="mx-8 border-t border-gray-200" />
                <div class="px-8 py-5">
                    <h4
                        class="text-xs font-bold tracking-[1.4px] text-mm-dark uppercase"
                    >
                        Included Services
                    </h4>
                    <ul class="mt-4 space-y-3">
                        <li
                            v-for="service in includedServices"
                            :key="service"
                            class="flex items-center gap-3"
                        >
                            <CircleCheck
                                class="size-5 shrink-0 text-mm-green"
                            />
                            <span class="text-sm text-mm-dark">{{
                                service
                            }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Optional Services -->
                <div class="mx-8 border-t border-gray-200" />
                <div class="px-8 py-5">
                    <h4
                        class="text-xs font-bold tracking-[1.4px] text-mm-dark uppercase"
                    >
                        Optional Services
                    </h4>
                    <div
                        v-if="
                            signup.optional_services.includes(
                                'flower_bed_cleaning',
                            ) ||
                            signup.optional_services.includes('bush_trimming')
                        "
                        class="mt-4 rounded-lg bg-mm-green px-4 py-3 text-sm font-semibold text-white"
                    >
                        A customer sales representative will contact you about
                        these services.
                    </div>
                    <div class="mt-4 space-y-3">
                        <label
                            v-for="service in optionalServices"
                            :key="service.value"
                            class="flex cursor-pointer items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                :checked="
                                    signup.optional_services.includes(
                                        service.value,
                                    )
                                "
                                class="peer sr-only"
                                @change="toggleOptionalService(service.value)"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs transition-colors"
                                :class="
                                    signup.optional_services.includes(
                                        service.value,
                                    )
                                        ? 'border-mm-green bg-mm-green'
                                        : ''
                                "
                            >
                                <svg
                                    v-if="
                                        signup.optional_services.includes(
                                            service.value,
                                        )
                                    "
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm text-mm-dark">
                                {{ service.label }}
                                <span
                                    v-if="service.value === 'bag_grass'"
                                    class="text-mm-green"
                                    >(+$10)</span
                                >
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex items-center justify-center gap-4">
            <button
                type="button"
                class="cursor-pointer rounded-md border-2 border-gray-300 px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-mm-dark uppercase transition-colors hover:border-gray-400"
                @click="$emit('back')"
            >
                Back
            </button>
            <button
                type="button"
                class="cursor-pointer rounded-md bg-mm-green px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
                @click="$emit('next')"
            >
                Next
            </button>
        </div>
    </div>
</template>
